import type Luminary from '@lumen5/luminary';
import type { OpaqueVideoData } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { computeSceneShortIds, resolveSceneById } from './scene-addressing';

import type { RootState } from 'js/src/libs/RootState';

/**
 * Whether a `scene_design_id` argument names a scene to copy rather than a design. Scene ids are
 * hex UUIDs (or the hex prefix the video state shows); design ids are `template:name`, so the two
 * never collide.
 */
export function isSceneIdReference(value: string): boolean {
    return /^[0-9a-f][0-9a-f-]{3,}$/i.test(value.trim());
}

/** A scene whose data can be copied onto a newly added one. */
export interface SceneCopySource {
    sceneData: OpaqueVideoData;
    /** The template the scene's `themeRef_` colour, font and logo references point into. */
    templateData: OpaqueVideoData;
    sceneDesign: string;
    /**
     * The aspect ratio the scene's positions were laid out for. Saved positions are only valid in
     * that ratio, so a copy into a video of another ratio would come out garbled.
     */
    aspectRatio: string;
    /** The full id of the scene being copied. */
    sceneId: string;
    /** Whether the scene comes from a fetched reference rather than the video being edited. */
    fromReference: boolean;
}

/**
 * The scenes of every reference video fetched this session, by the scene_id its annotation shows
 * and by full id. Module-level like the media registry: the reference is fetched by one tool and
 * copied from by others, and ids are UUID prefixes, so references never collide.
 */
const referenceScenes = new Map<string, SceneCopySource>();

/** A scene copied from a reference: which reference scene it came from. */
interface ReferenceCopy {
    sourceSceneId: string;
}

/**
 * Scenes in the video being edited that were copied from a reference, by their own id. Lint uses it
 * to recognise findings the reference itself has.
 */
const referenceCopies = new Map<string, ReferenceCopy>();

/** The reference scene a scene was copied from, if it was copied from one. */
export function referenceCopyOf(sceneId: string): ReferenceCopy | undefined {
    return referenceCopies.get(sceneId);
}

/** Make a fetched reference's scenes copyable by the scene_ids its annotation shows. */
export function registerReferenceScenes(
    videoData: OpaqueVideoData,
    templateData: OpaqueVideoData,
): void {
    const slides = ((videoData as { slides?: { id?: string; sceneDesign?: string }[] }).slides ??
        []) as (OpaqueVideoData & { id?: string; sceneDesign?: string })[];
    const shortIds = computeSceneShortIds(
        slides.map((slide) => slide.id).filter((id): id is string => typeof id === 'string'),
    );
    for (const slide of slides) {
        if (!slide.id || !slide.sceneDesign) {
            continue;
        }
        const source = {
            sceneData: slide,
            templateData,
            sceneDesign: slide.sceneDesign,
            aspectRatio: String((videoData as { aspectRatio?: string }).aspectRatio),
            sceneId: slide.id,
            fromReference: true,
        };
        referenceScenes.set(slide.id, source);
        const shortId = shortIds.get(slide.id);
        if (shortId) {
            referenceScenes.set(shortId, source);
        }
    }
}

/**
 * The scene a scene_id passed as `scene_design_id` names: one in the video being edited, else one in a fetched
 * reference. The video's own scenes win, so a scene the agent just built can be copied too.
 */
export function resolveSceneCopySource(
    luminary: Luminary,
    state: RootState,
    sceneId: string,
): SceneCopySource | { error: string } {
    const own = resolveSceneById(luminary, state, sceneId);
    if (!('error' in own)) {
        return {
            sceneData: own.scene.save(),
            templateData: luminary.template.save() as OpaqueVideoData,
            sceneDesign: own.scene.sceneDesign,
            aspectRatio: String(luminary.aspectRatio),
            sceneId: own.scene.id,
            fromReference: false,
        };
    }
    const reference = referenceScenes.get(sceneId.trim().toLowerCase());
    if (!reference) {
        return {
            error: `scene "${sceneId}" is not a scene_id in this video or in a reference video fetched this session.`,
        };
    }
    const [referenceTemplateId] = reference.sceneDesign.split(':');
    if (referenceTemplateId !== luminary.template.id) {
        return {
            error: `scene "${sceneId}" uses the ${referenceTemplateId} template, but this video uses ${luminary.template.id}, so it cannot be copied. Pick the closest scene_design_id instead.`,
        };
    }
    if (reference.aspectRatio !== String(luminary.aspectRatio)) {
        return {
            error: `scene "${sceneId}" is laid out for a ${reference.aspectRatio} video, but this video is ${luminary.aspectRatio}, so it cannot be copied. Use its design id (${reference.sceneDesign}) instead.`,
        };
    }
    return reference;
}

/**
 * Load a copy of `source` onto a scene that was just created for it, keeping the new scene's own
 * id, scene group, duration and category — a voice-driven scene's timing comes from the words it
 * covers, and its category from the design it was placed on, not from the scene it copies.
 *
 * `duplicateSceneData` remaps the source's template references onto this video's template, and new
 * asset ids keep a scene copied twice from sharing asset ids.
 */
export function loadSceneCopy(luminary: Luminary, sceneId: string, source: SceneCopySource): void {
    const target = luminary.getSceneById(sceneId);
    if (!target) {
        throw new Error(`Scene ${sceneId} to copy onto was not found`);
    }
    const { duration, sceneCategory } = target;
    const copy = VideoData.duplicateSceneData(
        source.sceneData,
        source.templateData,
        target.sceneDesign,
        luminary.template.save() as OpaqueVideoData,
    ) as OpaqueVideoData & { id?: string; sceneGroupId?: string };
    copy.id = target.id;
    copy.sceneGroupId = target.sceneGroup?.id;

    target.clear();
    target.load(copy);
    target.generateNewAssetIds();
    target.duration = duration;
    if (sceneCategory) {
        target.sceneCategory = sceneCategory;
    }

    if (source.fromReference) {
        referenceCopies.set(target.id, { sourceSceneId: source.sceneId });
    }
}
